import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Sparkles, Loader2, AlertTriangle, RefreshCw, TrendingUp, ListOrdered, Package, Scale } from 'lucide-react';

const ACCENT = '#A855F7';

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;
const api = async (url, method = 'GET') => {
    const r = await fetch(url, { method, headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf() } });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw data;
    return data;
};

// Sekce výstupu v pořadí, v jakém dávají smysl při čtení: nejdřív peníze.
const SEKCE = [
    { klic: 'ceny_a_marze', nadpis: 'Ceny a marže', Icon: TrendingUp },
    { klic: 'postupy',      nadpis: 'Postupy',      Icon: ListOrdered },
    { klic: 'suroviny',     nadpis: 'Suroviny',     Icon: Package },
    { klic: 'porcovani',    nadpis: 'Porcování',    Icon: Scale },
];

const ZAVAZNOST = {
    vysoka:  { label: 'Vysoká',  barva: '#EF4444' },
    stredni: { label: 'Střední', barva: '#F59E0B' },
    nizka:   { label: 'Nízká',   barva: '#3B82F6' },
};

const fmtCas = (v) => (v ? new Date(v).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

/**
 * AI analýza kategorie — srovnání cen, postupů a surovin napříč recepturami.
 *
 * Analýza běží ve frontě (nad velkou kategorií trvá minuty), takže se po
 * spuštění dotazujeme na stav, dokud nedoběhne.
 */
export default function AiAnalyzaModal({ open, kategorie, onClose, isDark }) {
    const [stav, setStav] = useState(null);       // { analyza, zastarala }
    const [nacitam, setNacitam] = useState(false);
    const [chyba, setChyba] = useState('');
    const tikRef = useRef(null);

    const mutedColor = isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-muted, #94a3b8)';
    const secondaryColor = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const borderColor = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';

    const nacti = useCallback(async () => {
        if (!kategorie?.id) return;
        try {
            const d = await api(`/api/lami/analyza/kategorie/${kategorie.id}`);
            setStav(d);
            return d;
        } catch (e) {
            setChyba(e.error || 'Analýzu se nepodařilo načíst.');
        }
    }, [kategorie?.id]);

    useEffect(() => {
        if (!open) { clearInterval(tikRef.current); return; }
        setChyba(''); setStav(null);
        nacti();
        return () => clearInterval(tikRef.current);
    }, [open, nacti]);

    // Dokud analýza běží, ptáme se po deseti vteřinách. Model nad velkou
    // kategorií přemýšlí i minuty, kratší interval by jen zatěžoval server.
    useEffect(() => {
        clearInterval(tikRef.current);
        if (!open || stav?.analyza?.stav !== 'bezi') return;
        tikRef.current = setInterval(nacti, 10000);
        return () => clearInterval(tikRef.current);
    }, [open, stav?.analyza?.stav, nacti]);

    const spust = async () => {
        setNacitam(true); setChyba('');
        try {
            await api(`/api/lami/analyza/kategorie/${kategorie.id}`, 'POST');
            await nacti();
        } catch (e) {
            setChyba(e.error || 'Analýzu se nepodařilo spustit.');
        }
        setNacitam(false);
    };

    if (!open) return null;

    const a = stav?.analyza;
    const bezi = a?.stav === 'bezi';
    const v = a?.stav === 'hotovo' ? a.vysledek : null;

    const zjisteniCelkem = v ? SEKCE.reduce((n, s) => n + (v[s.klic]?.length || 0), 0) : 0;

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}>
            <div onClick={e => e.stopPropagation()} className="rounded-2xl flex flex-col"
                style={{
                    width: 780, maxWidth: '95vw', height: '85vh',
                    background: isDark ? 'rgba(24,22,32,0.97)' : '#fff',
                    border: `1px solid ${borderColor}`, overflow: 'hidden',
                }}>

                <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: `1px solid ${borderColor}` }}>
                    <div className="flex items-center gap-2 min-w-0">
                        <Sparkles size={16} style={{ color: ACCENT, flexShrink: 0 }} />
                        <h3 className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                            AI analýza — {kategorie?.nazev}
                        </h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={spust} disabled={bezi || nacitam}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
                            style={{ background: bezi || nacitam ? 'transparent' : ACCENT, color: bezi || nacitam ? mutedColor : '#fff', border: bezi || nacitam ? `1px solid ${borderColor}` : 'none', cursor: bezi || nacitam ? 'default' : 'pointer' }}>
                            {bezi || nacitam ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                            {bezi ? 'Analyzuji…' : (a ? 'Spustit znovu' : 'Spustit analýzu')}
                        </button>
                        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: secondaryColor }}><X size={18} /></button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-5">
                    {chyba && (
                        <div className="rounded-xl p-3 mb-4 flex items-start gap-2" style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.25)' }}>
                            <AlertTriangle size={15} style={{ color: '#EF4444', flexShrink: 0, marginTop: 1 }} />
                            <span className="text-xs" style={{ color: '#EF4444' }}>{chyba}</span>
                        </div>
                    )}

                    {!a && !chyba && (
                        <div className="flex flex-col items-center justify-center text-center h-full gap-3" style={{ color: mutedColor }}>
                            <Sparkles size={34} style={{ opacity: 0.35 }} />
                            <p className="text-sm" style={{ color: secondaryColor }}>Kategorie zatím nebyla analyzována.</p>
                            <p className="text-xs" style={{ maxWidth: 420 }}>
                                Analýza porovná ceny, marže, postupy a suroviny napříč všemi recepturami
                                kategorie a najde, co vybočuje. Chvíli to trvá — poběží na pozadí.
                            </p>
                        </div>
                    )}

                    {bezi && (
                        <div className="flex flex-col items-center justify-center text-center h-full gap-3" style={{ color: mutedColor }}>
                            <Loader2 size={28} className="animate-spin" style={{ color: ACCENT }} />
                            <p className="text-sm" style={{ color: secondaryColor }}>Analyzuji {a.pocet_receptur} receptur…</p>
                            <p className="text-xs">Můžeš okno zavřít, výsledek tu na tebe počká.</p>
                        </div>
                    )}

                    {a?.stav === 'chyba' && (
                        <div className="rounded-xl p-4" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.22)' }}>
                            <p className="text-sm font-semibold mb-1" style={{ color: '#EF4444' }}>Analýza selhala</p>
                            <p className="text-xs" style={{ color: secondaryColor }}>{a.chyba}</p>
                        </div>
                    )}

                    {v && (
                        <>
                            {stav?.zastarala && (
                                <div className="rounded-lg px-3 py-2 mb-4 text-xs" style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.25)', color: '#F59E0B' }}>
                                    Receptury se od téhle analýzy změnily — výsledek už nemusí sedět.
                                </div>
                            )}

                            <div className="rounded-xl p-4 mb-5" style={{ background: isDark ? 'rgba(168,85,247,0.08)' : 'rgba(168,85,247,0.06)', border: `1px solid ${ACCENT}33` }}>
                                <p className="text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: ACCENT }}>Souhrn</p>
                                <p className="text-sm" style={{ color: 'var(--text-primary)', lineHeight: 1.55 }}>{v.souhrn}</p>
                            </div>

                            {zjisteniCelkem === 0 && (
                                <p className="text-sm text-center py-6" style={{ color: mutedColor }}>
                                    Žádná konkrétní zjištění — kategorie vypadá konzistentně.
                                </p>
                            )}

                            {SEKCE.map(({ klic, nadpis, Icon }) => {
                                const polozky = v[klic] || [];
                                if (!polozky.length) return null;
                                return (
                                    <div key={klic} className="mb-5">
                                        <h4 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide mb-2" style={{ color: secondaryColor }}>
                                            <Icon size={13} /> {nadpis}
                                            <span style={{ color: mutedColor, fontWeight: 500 }}>({polozky.length})</span>
                                        </h4>
                                        <div className="flex flex-col gap-2">
                                            {polozky.map((z, i) => {
                                                const zav = ZAVAZNOST[z.zavaznost] || ZAVAZNOST.nizka;
                                                return (
                                                    <div key={i} className="rounded-xl p-3.5" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)', border: `1px solid ${borderColor}`, borderLeft: `3px solid ${zav.barva}` }}>
                                                        <div className="flex items-start justify-between gap-2 mb-1">
                                                            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{z.nadpis}</span>
                                                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0"
                                                                style={{ background: `${zav.barva}1F`, color: zav.barva }}>{zav.label}</span>
                                                        </div>
                                                        <p className="text-xs mb-2" style={{ color: secondaryColor, lineHeight: 1.5 }}>{z.zjisteni}</p>
                                                        <p className="text-xs" style={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>
                                                            <span style={{ color: mutedColor }}>Doporučení: </span>{z.doporuceni}
                                                        </p>
                                                        {z.dotcene?.length > 0 && (
                                                            <div className="flex flex-wrap gap-1 mt-2">
                                                                {z.dotcene.map((d, j) => (
                                                                    <span key={j} className="text-[10px] px-1.5 py-0.5 rounded-md"
                                                                        style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: secondaryColor }}>{d}</span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>

                {a?.stav === 'hotovo' && (
                    <div className="px-5 py-2 text-[11px] flex items-center justify-between" style={{ borderTop: `1px solid ${borderColor}`, color: mutedColor }}>
                        <span>{a.pocet_receptur} receptur · {fmtCas(a.created_at)}</span>
                        <span>{a.model}{a.tokenu_vstup ? ` · ${Math.round(a.tokenu_vstup / 1000)}k vstup / ${Math.round((a.tokenu_vystup || 0) / 1000)}k výstup` : ''}</span>
                    </div>
                )}
            </div>
        </div>
    );
}
